// YERİ: src/app/core/i18n/transloco.config.ts
//
// Portal arayüz metinlerinin sözlük katmanı (Batch 10).
//
// ── Neden yalnız PORTAL, ama yine de KÖKTE sağlanıyor ──────────────────────
// Vitrin metinleri CMS'ten geliyor (WebPage/WebContentBlock), auth ekranları
// kendi tip-güvenli sözlüğünü kullanıyor (domains/auth/auth-i18n). Sözlük
// yalnız panel için var.
//
// Yine de `providePortalTransloco()` app.config.ts'te, '/portal' rotasında
// DEĞİL: TranslocoService `providedIn: 'root'` tanımlı, rota seviyesinde
// sağlansa bile kök injector'da oluşturulmaya çalışılır, TRANSLOCO_TRANSPILER'ı
// orada bulamaz (NG0201) ve ardından döngüye girer (NG0200) — localeReadyGuard
// patlar, panel hiç açılmaz. Kökte olması vitrine yük bindirmiyor: dosya ancak
// `load()` çağrılınca iniyor ve vitrin Transloco kullanmıyor.
//
// ── Dil nereden geliyor ────────────────────────────────────────────────────
// LOCALE_ID ile AYNI kaynaktan: kullanıcının Görünüm ayarlarındaki tercihi
// (localStorage 'portal_theme_v1'). İki ayrı kaynak olsaydı tarihler bir dilde,
// metinler başka dilde çıkardı.
//
// ── Sözlük dosyaları ───────────────────────────────────────────────────────
// public/i18n/portal/<kod>.json  →  /i18n/portal/tr.json
// Kaynak dil TÜRKÇE: metinler Türkçe yazılıyor (CLAUDE.md), çeviriler ondan
// türüyor. Eksik anahtarda Transloco Türkçe'ye düşer — anahtar adı göstermek
// yerine anlaşılır bir metin çıkar.

import { HttpClient } from '@angular/common/http';
import { Injectable, inject, isDevMode } from '@angular/core';
import {
  Translation,
  TranslocoLoader,
  provideTransloco,
} from '@jsverse/transloco';
import { APP_LOCALES, DEFAULT_LOCALE, findLocale } from './locale';

/** PortalThemeService'in localStorage anahtarı — portal-locale.ts'te de aynı sabit. */
const THEME_STORAGE_KEY = 'portal_theme_v1';

/** Sözlüğün kaynak dili. Çevirisi eksik anahtar buraya düşer. */
export const SOURCE_LANG = 'tr';

// `providedIn: 'root'` YOK ve olmamalı: yükleyiciyi `provideTransloco({loader})`
// zaten kaydediyor. İkisi birden olunca yükleyici bir kez kök injector'da, bir
// kez Transloco'nun kendi kaydında çözülüyor ve TranslocoService ile arasında
// döngü çıkıyor (NG0200).
@Injectable()
export class PortalTranslationLoader implements TranslocoLoader {
  private http = inject(HttpClient);

  getTranslation(lang: string) {
    return this.http.get<Translation>(`/i18n/portal/${lang}.json`);
  }
}

/**
 * Kullanıcının panel dili — Transloco'nun kod biçiminde ('en-ca', 'tr').
 *
 * `readStoredPortalLocale` Angular'ın kimliğini ('en-CA') döndürüyor; sözlük
 * dosyaları küçük harfli kodla adlandırıldığı için burada `code` alanını
 * kullanıyoruz. Aynı yanlışı iki kez yapmamak için tek yerde çözülüyor.
 */
export function readPortalLangCode(): string {
  if (typeof localStorage === 'undefined') return findLocale(DEFAULT_LOCALE).code;

  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    const code = raw ? (JSON.parse(raw) as { language?: string }).language : null;
    return findLocale(code).code;
  } catch {
    // Bozuk JSON tüm portalı açılmaz hâle getirmemeli.
    return findLocale(DEFAULT_LOCALE).code;
  }
}

export function providePortalTransloco() {
  return provideTransloco({
    config: {
      availableLangs: APP_LOCALES.map((l) => l.code),
      defaultLang: readPortalLangCode(),
      fallbackLang: SOURCE_LANG,
      // Eksik anahtarda kaynak dile düş — kullanıcı 'portal.nav.crm' gibi bir
      // anahtar adı görmesin. Yeni bir dil eksik çevrilmişken de panel okunur.
      missingHandler: { useFallbackTranslation: true, allowEmpty: false, logMissingKey: isDevMode() },
      reRenderOnLangChange: false,
      prodMode: !isDevMode(),
    },
    loader: PortalTranslationLoader,
  });
}
